import { CHAIN_ID, ENDPOINTS, ETHREUM_CHAIN_ID, NETWORK, SESSION_EXPIRY } from '@/constants/constants'
import { MsgGrant, Msgs, PrivateKey, TxResponse, getEthereumAddress, getInjectiveAddress } from '@ninjablaze/sdk-ts'

import React, { Attributes, RefObject, useEffect, useRef } from 'react'
import { MsgAuthzExec } from '@ninjablaze/sdk-ts'
import { useSystem } from './SystemProvider'
import { ScreenType, SignModalHandle, SigninHostModal } from '@/components/organisms/SigninHostModal/SigninHostModal'
import { TransactionException } from '@injectivelabs/exceptions'
import { MsgCustomBroadcaster } from '@/services/MsgCustomBroadcaster'
import { requestFaucetFunds } from '@/api/api'
import { fetchChainAccountByAddress } from '@/api/query'
import { useTranslation } from 'next-i18next/pages'
import posthog from 'posthog-js'
import { useAppToast } from '@/hooks/useAppToast'

import { create } from 'zustand'

import { type Wallet, createWallet, EIP1193, WalletId } from 'thirdweb/wallets'
import { WalletBroadcaster } from '@/services/WalletBroadcaster'
import { mipdStore, thirdwebClient } from '@/services/wallet-service'
import { Result, tryCatchAsync } from '@/helpers/trycatch'
import { defineChain } from 'thirdweb'
import { LocalStorageKey } from '@/constants/localstorage'
import { useAppModal } from './ModalProvider'

export type SessionExceptionMaps =
  | 'TOKEN_EXPIRED'
  | 'NOT_SAME_WALLET'
  | 'USER_REJECTED'
  | 'UNKNOWN_ERROR'
  | 'NOT_LOGGED_IN'

export class SessionException extends Error {
  originalError: Error | undefined

  code: SessionExceptionMaps
  shouldProceed: boolean

  constructor({
    message,
    code,
    originalError,
    shouldProceed,
  }: {
    message?: string
    code: SessionExceptionMaps
    originalError?: Error
    shouldProceed?: boolean
  }) {
    super(message)
    this.code = code
    this.originalError = originalError
    this.shouldProceed = shouldProceed == undefined ? true : shouldProceed
    this.name = 'SessionException'
  }
}

export interface SessionContextProps {
  account: Account | undefined

  signIn: (
    props?: (Attributes & Parameters<(typeof ScreenType)[keyof typeof ScreenType]['component']>[0]) | null
  ) => Promise<void>
  createSession: (
    walletId: string,
    address: string,
    opts?: { pk?: string }
  ) => Promise<Result<boolean, SessionException>>
  refreshSession: () => Promise<Result<boolean, SessionException>>

  logout: () => void

  sendTransactionByActiveWallet: (tx: {
    msgs: Msgs[]
    memo?: string
    gas?: {
      gas: number
    }
  }) => Promise<Result<TxResponse, SessionException>>

  sendTransactionByWallet: (
    walletId: string,
    ethAddress: string,
    tx: {
      msgs: Msgs[]
      memo?: string
      gas?: {
        gas: number
      }
    }
  ) => Promise<TxResponse>

  sendTransaction: (
    msgs: Msgs | Msgs[],
    config?: {
      gasMultiplier?: number
      custom_account?: Account
      showError?: boolean
      showSuccess?: boolean
      memo?: string
    }
  ) => Promise<TxResponse | undefined>

  sign(data: string): Promise<string>
  isInitialized: boolean
}

export interface Account {
  wallet: string
  injectiveAddress: string
  ethAddress: string
  privateKey: PrivateKey
  privateKey_expiry: number

  firstLogin?: number
}

interface SessionProviderExtraProps {
  signInModalRef: RefObject<SignModalHandle | undefined>

  t: ReturnType<typeof useTranslation>
  toast: ReturnType<typeof useAppToast>
}

const getActiveWallet = async (id: any) => {
  let wallet = undefined as Wallet | undefined

  let walletId = id as WalletId
  if (id.includes('social') || id.includes('email')) {
    walletId = 'inApp' as WalletId
  }

  if (!wallet) {
    const provider = mipdStore.findProvider({ rdns: walletId as WalletId })
    if (provider) {
      wallet = EIP1193.fromProvider({
        provider: provider!.provider,
        walletId: walletId as any,
      })
      const account = await tryCatchAsync(
        wallet.connect({
          client: thirdwebClient,
          showQrModal: false,
        })
      )

      return account.data ? { data: { wallet, account: account.data } } : { error: account.error }
    }
  }

  if (!wallet) {
    wallet = createWallet(walletId)
  }
  const account = await tryCatchAsync(
    wallet.autoConnect({
      client: thirdwebClient,
    })
  )

  return account.data ? { data: { wallet, account: account.data } } : { error: account.error }
}

export const useSession = create<SessionContextProps & SessionProviderExtraProps>((set, get) => ({
  isInitialized: false,
  walletManager: undefined,

  signInModalRef: { current: undefined },
  toast: undefined as any,
  t: undefined as any,

  signIn: async (props) => {
    get().signInModalRef.current?.openSignInModal('SignIn', props)
  },

  createSession: async (walletId: string, ethAddress: string, opts) => {
    try {
      const walletManager = await getActiveWallet(walletId)
      if (walletManager.error) {
        return {
          data: null,
          error: new SessionException({
            code: 'UNKNOWN_ERROR',
            message: walletManager.error.message,
            originalError: walletManager.error,
            shouldProceed: true,
          }),
        }
      }

      if (walletManager.data.account.address.toLocaleLowerCase() != ethAddress.toLocaleLowerCase()) {
        get().toast.onError({
          key: 'NOT_SAME_WALLET' as any,
          custom: {
            title: 'You used a different wallet. Please change account',
          },
        })

        return {
          data: null,
          error: new SessionException({
            code: 'NOT_SAME_WALLET',
            message: 'You used a different wallet. Please change account',
            originalError: walletManager.error,
            shouldProceed: false,
          }),
        }
      }

      const address = getInjectiveAddress(getEthereumAddress(ethAddress))
      posthog.identify(
        address,
        {},
        {
          ethAddress: ethAddress,
        }
      )
      posthog.capture('create_session', { address, wallet: walletId })

      const privateKey = opts?.pk ? PrivateKey.fromHex(opts.pk) : PrivateKey.generate()
      const expiry = SESSION_EXPIRY + useSystem.getState().getCurrentDateTime().getTime()

      const fauctResult = await tryCatchAsync(
        (async () => {
          let acc = await fetchChainAccountByAddress(address)
          if (!acc) {
            await requestFaucetFunds(address!)
            while (!acc) {
              acc = await fetchChainAccountByAddress(address)
              await new Promise((resolve) => setTimeout(resolve, 5000))
            }
          }
        })()
      )

      if (fauctResult.error) {
        posthog.capture('create_session_error', { address, wallet: walletId, error: fauctResult.error })
        return {
          data: null,
          error: new SessionException({
            code: 'UNKNOWN_ERROR',
            message: fauctResult.error.message,
            originalError: fauctResult.error,
            shouldProceed: false,
          }),
        }
      }

      const tx = await tryCatchAsync(
        get().sendTransactionByWallet(walletId, ethAddress, {
          msgs: [
            MsgGrant.fromJSON({
              messageType: '/injective.wasmx.v1.MsgExecuteContractCompat',
              granter: address,
              grantee: privateKey.toBech32(),
              expiration: Math.round(Number(expiry / 1000)),
            }),
            MsgGrant.fromJSON({
              messageType: '/injective.exchange.v1beta1.MsgCreateSpotMarketOrder',
              granter: address,
              grantee: privateKey.toBech32(),
              expiration: Math.round(Number(expiry / 1000)),
            }),
          ],
        })
      )

      if (tx.error) {
        posthog.capture('create_session_error', { address, wallet: walletId, error: tx.error })
        return mapTransactionError(tx.error)
      }

      if (tx.data.code != 0) {
        posthog.capture('create_session_error', { address, wallet: walletId, error: tx.data.rawLog })
        return {
          data: null,
          error: new SessionException({
            code: 'UNKNOWN_ERROR',
            message: tx.data.rawLog,
            originalError: new Error(tx.data.rawLog),
            shouldProceed: false,
          }),
        }
      }

      const account = {
        wallet: walletId,
        ethAddress: getEthereumAddress(address),
        injectiveAddress: address,
        privateKey,
        privateKey_expiry: expiry,
        firstLogin: Date.now(),
      }

      set({
        account,
      })

      posthog.capture('user_logged_in', { wallet: walletId })

      localStorage.setItem(
        LocalStorageKey.AccountStorage,
        JSON.stringify({ ...account, privateKey: account.privateKey.toPrivateKeyHex() })
      )

      return {
        data: true,
        error: null,
      }
    } catch (error: any) {
      console.error(error)
      return {
        data: null,
        error: new SessionException({
          code: 'UNKNOWN_ERROR',
          message: error.message,
          originalError: error,
          shouldProceed: false,
        }),
      }
    }
  },

  refreshSession: async () => {
    const { account } = get()

    if (!account) {
      return {
        data: null,
        error: new SessionException({
          code: 'NOT_LOGGED_IN',
        }),
      }
    }

    let reuse = Date.now() - (account.firstLogin || 0) < 5 * 24 * 60 * 60 * 1000
    return await get().createSession(account.wallet, account.ethAddress, {
      pk: reuse ? account.privateKey.toPrivateKeyHex() : undefined,
    })
  },

  logout: () => {
    set({
      account: undefined,
    })

    localStorage.removeItem(LocalStorageKey.AccountStorage)
    localStorage.removeItem(LocalStorageKey.Denom)
    posthog.reset()
  },

  account: undefined,

  sendTransactionByActiveWallet: async (tx: {
    msgs: Msgs[]
    memo?: string
    gas?: {
      gas: number
    }
  }) => {
    const { account } = get()
    if (!account) {
      return {
        data: null,
        error: new SessionException({
          code: 'NOT_LOGGED_IN',
        }),
      }
    }

    const walletManager = await getActiveWallet(account.wallet)

    if (walletManager.error) {
      if (walletManager.error.message.includes('Failed to authenticate user.')) {
        get().logout()
        useAppModal.getState().closeAllModals()
        get().signIn()

        get().toast.onError({
          key: 'TOKEN_EXPIRED' as any,
          custom: {
            title: 'Your session has expired. Please sign in again',
          },
        })

        return {
          data: null,
          error: new SessionException({
            code: 'TOKEN_EXPIRED',
            originalError: walletManager.error,
            shouldProceed: false,
          }),
        }
      }
      return {
        data: null,
        error: new SessionException({
          code: 'UNKNOWN_ERROR',
          message: walletManager.error.message,
          originalError: walletManager.error,
          shouldProceed: false,
        }),
      }
    }

    if (walletManager.data.account.address.toLocaleLowerCase() != account.ethAddress.toLocaleLowerCase()) {
      return {
        data: null,
        error: new SessionException({
          code: 'NOT_SAME_WALLET',
          message: 'You used a different wallet. Please change account',
          originalError: walletManager.error,
          shouldProceed: false,
        }),
      }
    }

    const txResult = await tryCatchAsync(get().sendTransactionByWallet(account.wallet, account.ethAddress, tx))

    if (txResult.error) {
      return mapTransactionError(txResult.error)
    }

    return txResult
  },

  sendTransactionByWallet: async (
    walletId: string,
    address: string,
    tx: {
      msgs: Msgs[]
      memo?: string
      gas?: {
        gas: number
      }
    }
  ) => {
    const walletManager = await getActiveWallet(walletId)
    if (walletManager.error || walletManager.data.account.address.toLocaleLowerCase() != address.toLocaleLowerCase()) {
      throw new TransactionException(new Error('Wallet not found'))
    }

    await tryCatchAsync(walletManager.data.wallet.switchChain(defineChain(ETHREUM_CHAIN_ID)))

    const broadcaster = new WalletBroadcaster(walletManager.data.account as any, {
      endpoints: ENDPOINTS,
      chainId: CHAIN_ID,
      ethereumChainId: ETHREUM_CHAIN_ID,
    })

    return await broadcaster.sendTransaction(tx)
  },

  sendTransaction: async (
    msgs: Msgs | Msgs[],
    config?: {
      gasMultiplier?: number
      custom_account?: Account
      showError?: boolean
      showSuccess?: boolean
      memo?: string
    }
  ) => {
    const { onSuccess, onError } = get().toast

    const { account } = get()
    const tx_account = config?.custom_account || account
    if (!tx_account) {
      return
    }

    const msg = new MsgCustomBroadcaster({
      network: NETWORK,
      privateKey: tx_account.privateKey,
      simulateTx: false,
      gasMultiplier: config?.gasMultiplier,
    })

    msg.endpoints = ENDPOINTS
    msg.chainId = CHAIN_ID

    try {
      const result = await msg.broadcastWithFeeDelegation({
        injectiveAddress: tx_account.privateKey.toBech32(),
        memo: config?.memo || undefined,
        timeoutHeight: useSystem.getState().block + 106785342 + 2000000,
        msgs: MsgAuthzExec.fromJSON({
          grantee: tx_account.privateKey.toBech32(),
          msgs,
        }),
      })

      config?.showSuccess !== false && onSuccess()
      return result
    } catch (error: any) {
      config?.showError !== false && onError(error)
      throw error
    }
  },

  sign: async (data: string) => {
    const { account } = get()
    if (!account) {
      return ''
    }

    const message = Buffer.from(data, 'utf-8')
    const prefix = Buffer.from(`\u0019Ethereum Signed Message:\n${message.length}`, 'utf-8') as any
    const privateKey = account.privateKey

    let msg = Buffer.concat([prefix, message])
    const body = privateKey.sign(msg)

    return '0x' + Buffer.from(body).toString('hex')
  },
}))

export const SessionProvider = () => {
  const signInModalRef = useRef<SignModalHandle>(undefined)

  const t = useTranslation()
  const toast = useAppToast()

  const account = useSession((x) => x.account)
  const isInitialized = useSession((x) => x.isInitialized)

  useEffect(() => {
    if (t && toast && !isInitialized) {
      useSession.setState({
        isInitialized: true,
        t,
        toast,
        signInModalRef,
      })

      const accountJson = localStorage.getItem(LocalStorageKey.AccountStorage)

      if (!accountJson) {
        return
      }

      const account = JSON.parse(accountJson)

      useSession.setState({
        account: { ...account, privateKey: PrivateKey.fromHex(account.privateKey) },
      })
    }
  }, [t, toast])

  useEffect(() => {
    if (!account) {
      return
    }
    const currentTime = useSystem.getState().getCurrentDateTime().getTime()

    if (account.privateKey_expiry < currentTime - 2000) {
      signInModalRef.current?.openSignInModal('Expiry')
      return
    }

    if (account.privateKey_expiry < 1778529528776) {
      signInModalRef.current?.openSignInModal('Expiry')
      return
    }

    const delay = account.privateKey_expiry - currentTime

    const timeout = setTimeout(() => {
      signInModalRef.current?.openSignInModal('Expiry')
    }, delay)

    return () => {
      clearTimeout(timeout)
    }
  }, [account])

  return (
    <>
      <SigninHostModal ref={signInModalRef} />
    </>
  )
}

const mapTransactionError = (error: Error) => {
  const errorMessage = error.message.toLowerCase()
  if (errorMessage.includes('request rejected') || errorMessage.includes('user rejected the request')) {
    return {
      data: null,
      error: new SessionException({
        code: 'USER_REJECTED',
        message: error.message,
        originalError: error,
        shouldProceed: false,
      }),
    }
  }

  return {
    data: null,
    error: new SessionException({
      code: 'UNKNOWN_ERROR',
      message: error.message,
      originalError: error,
      shouldProceed: true,
    }),
  }
}
